/**
 * @module DatosTrabajador
 */

import AddIcon from '@mui/icons-material/Add'
import { Alert, Box, LinearProgress, Typography } from '@mui/material'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import BotonSolicitudInterna from 'features/solicitudes/components/BotonSolicitudInterna'
import i18n from 'lang/idiomas'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import FilaExperiencia from '../components/FilaExperiencia'
import FilaFormacion from '../components/FilaFormacion'
import useFetchUserExperience from '../hooks/useFetchExperience'
import useFetchUserFormation from '../hooks/useFetchUserFormation'

/**
 * Component to show the experience of the Worker
 */
const Curriculum = () => {
  const location = useLocation()

  const [user, _] = useState(location?.state?.user ?? null)
  const { response, loading: loadingFormation } = useFetchUserFormation(user)
  const { response: experienceResponse } = useFetchUserExperience(user)

  const [experienciaTrabajador, setExperienciaTrabajador] = useState(experienceResponse)
  useEffect(() => setExperienciaTrabajador(experienceResponse), [experienceResponse])

  const [formacionExterna, setFormacionExterna] = useState(response)
  const [isLoadingFormation, setLoadingFormation] = useState(loadingFormation)

  useEffect(() => {
    setFormacionExterna(response)
  }, [response])
  useEffect(() => {
    setLoadingFormation(loadingFormation)
  }, [loadingFormation])

  return (
    <>
      <Box sx={{ my: 3, display: 'flex', gap: 2 }}>
        <Typography variant="h3">{i18n.t('curricul', { defaultValue: 'Curriculum' })}</Typography>
      </Box>
      <Stack>
        <Grid className="outline-paper" container spacing={2}>
          <Grid item size={{ xs: 12 }}>
            <Card sx={{ flexGrow: 1 }}>
              <CardHeader
                title={i18n.t('explab', { defaultValue: 'Experiencia laboral' })}
                action={
                  !user ? (
                    <BotonSolicitudInterna
                      titulo={i18n.t('anadir', { defaultValue: 'Añadir' })}
                      id_solicitud_pe="EXPIN"
                      buttonStartIcon={<AddIcon />}
                      buttonVariant="text"
                      buttonSize="small"
                    />
                  ) : null
                }
              />
              <CardContent>
                <TableContainer component={Paper}>
                  <Table aria-label="tabla formación">
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>{i18n.t('puesto', { defaultValue: 'Puesto' })}</TableCell>
                        <TableCell>{i18n.t('activid', { defaultValue: 'Actividad' })}</TableCell>
                        <TableCell>{i18n.t('fechini', { defaultValue: 'Fecha inicio' })}</TableCell>
                        <TableCell>{i18n.t('fechfin', { defaultValue: 'Fecha fin' })}</TableCell>
                        <TableCell>{i18n.t('duracion', { defaultValue: 'Duración' })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!experienciaTrabajador ? (
                        <></>
                      ) : (
                        experienciaTrabajador.map((exp) => {
                          const finicio = new Date(
                            exp?.f_inicio?.replace(/(\d{2})-(\d{2})-(\d{4})/, '$2/$1/$3'),
                          )
                          const ffin = new Date(
                            exp.f_fin?.replace(/(\d{2})-(\d{2})-(\d{4})/, '$2/$1/$3'),
                          )

                          const { fecha_inicio, fecha_fin, ...row } = exp

                          row.fecha_inicio = finicio.toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                          row.fecha_fin = ffin.toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })

                          return <FilaExperiencia key={row.id} row={row} />
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item size={{ xs: 12 }}>
            <Card sx={{ flexGrow: 1 }}>
              <CardHeader
                title={i18n.t('formacio', { defaultValue: 'Formación' })}
                action={
                  !user ? (
                    <BotonSolicitudInterna
                      titulo={i18n.t('anadir', { defaultValue: 'Añadir' })}
                      id_solicitud_pe="FORIN"
                      buttonStartIcon={<AddIcon />}
                      buttonVariant="text"
                      buttonSize="small"
                    />
                  ) : null
                }
              />
              <CardContent>
                <TableContainer component={Paper}>
                  {!formacionExterna ? (
                    <>
                      {isLoadingFormation ? (
                        <>
                          <LinearProgress />
                        </>
                      ) : (
                        <>
                          <Alert severity="info">
                            {i18n.t('noformac', {
                              defaultValue: 'No se ha encontrado ninguna formacion',
                            })}
                          </Alert>
                        </>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  <Table aria-label="tabla formación">
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>{i18n.t('titulo', { defaultValue: 'Título' })}</TableCell>
                        <TableCell>{i18n.t('materia', { defaultValue: 'Materia' })}</TableCell>
                        <TableCell>{i18n.t('fechini', { defaultValue: 'Fecha inicio' })}</TableCell>
                        <TableCell>{i18n.t('fechfin', { defaultValue: 'Fecha fin' })}</TableCell>
                        <TableCell>{i18n.t('duracion', { defaultValue: 'Duración' })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!formacionExterna ? (
                        <></>
                      ) : (
                        formacionExterna.map((formation, index) => {
                          const fixedStartDate = formation?.f_inicio?.replace(
                            /(\d{2})-(\d{2})-(\d{4})/,
                            '$2/$1/$3',
                          )
                          const fixedEndDate = formation.f_fin?.replace(
                            /(\d{2})-(\d{2})-(\d{4})/,
                            '$2/$1/$3',
                          )

                          const finicio = new Date(fixedStartDate)
                          const ffin = new Date(fixedEndDate)

                          /** @type {ExtendedRow} **/
                          const newRow = { ...formation, fecha_inicio: null, fecha_fin: null }

                          newRow.fecha_inicio = !isNaN(Date.parse(fixedStartDate))
                            ? finicio.toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })
                            : '---'
                          newRow.fecha_fin = !isNaN(Date.parse(fixedEndDate))
                            ? ffin.toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })
                            : '---'

                          return <FilaFormacion key={`Formation ${index}`} row={newRow} />
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </>
  )
}

export default Curriculum
